import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import type { LegendSwatch as Swatch } from "../../map/drawLegendSwatch";
import type { MapImages } from "../../map/drawMatchMap";
import type { MapColors } from "../../map/MapColors";
import type { MapScene } from "../../map/MapScene";
import { LegendSwatch } from "./LegendSwatch";

const LABEL = msg({ id: "analysis.map.legend", message: "Legend" });

type Group = { title: MessageDescriptor; items: { swatch: Swatch; label: MessageDescriptor }[] };

const GROUPS: Group[] = [
  {
    title: msg({ id: "analysis.map.legend.group.you", message: "You" }),
    items: [
      {
        swatch: "me",
        label: msg({ id: "analysis.map.legend.me", message: "you and your last minute" }),
      },
      {
        swatch: "heat",
        label: msg({
          id: "analysis.map.legend.heat",
          message: "where you were: brighter means longer",
        }),
      },
      {
        swatch: "myDeath",
        label: msg({ id: "analysis.map.legend.my_death", message: "your death" }),
      },
      {
        swatch: "myKill",
        label: msg({ id: "analysis.map.legend.my_kill", message: "your kill" }),
      },
    ],
  },
  {
    title: msg({ id: "analysis.map.legend.group.others", message: "Others" }),
    items: [
      { swatch: "mate", label: msg({ id: "analysis.map.legend.mate", message: "teammate" }) },
      { swatch: "enemy", label: msg({ id: "analysis.map.legend.enemy", message: "enemy" }) },
      {
        swatch: "mateDeath",
        label: msg({ id: "analysis.map.legend.mate_death", message: "teammate died" }),
      },
      {
        swatch: "enemyDeath",
        label: msg({ id: "analysis.map.legend.enemy_death", message: "enemy died" }),
      },
    ],
  },
  {
    title: msg({ id: "analysis.map.legend.group.camps", message: "Camps" }),
    items: [
      {
        swatch: "campMe",
        label: msg({ id: "analysis.map.legend.camp_mine", message: "farmed by you" }),
      },
      {
        swatch: "campTeam",
        label: msg({ id: "analysis.map.legend.camp_team", message: "farmed by teammates" }),
      },
      {
        swatch: "campMissed",
        label: msg({ id: "analysis.map.legend.camp_free", message: "nobody farmed it" }),
      },
      {
        swatch: "campEnemy",
        label: msg({ id: "analysis.map.legend.camp_enemy", message: "on the enemy half" }),
      },
    ],
  },
  {
    title: msg({ id: "analysis.map.legend.group.buildings", message: "Buildings" }),
    items: [
      {
        swatch: "guardian",
        label: msg({ id: "analysis.map.legend.guardian", message: "guardian" }),
      },
      { swatch: "walker", label: msg({ id: "analysis.map.legend.walker", message: "walker" }) },
      {
        swatch: "patron",
        label: msg({ id: "analysis.map.legend.patron", message: "patron and base" }),
      },
      {
        swatch: "destroyed",
        label: msg({ id: "analysis.map.legend.destroyed", message: "destroyed" }),
      },
    ],
  },
  {
    title: msg({ id: "analysis.map.legend.group.map", message: "On the map" }),
    items: [
      {
        swatch: "rune",
        label: msg({ id: "analysis.map.legend.bridge_rune", message: "bridge rune" }),
      },
      {
        swatch: "boss",
        label: msg({ id: "analysis.map.legend.boss", message: "mid-boss, dim — dead" }),
      },
      {
        swatch: "buff",
        label: msg({
          id: "analysis.map.legend.buff",
          message: "mid-boss buff: colour shows whose",
        }),
      },
      { swatch: "shop", label: msg({ id: "analysis.map.legend.shop", message: "shop" }) },
      {
        swatch: "zipline",
        label: msg({
          id: "analysis.map.legend.zipline",
          message: "zipline: colour shows whose half",
        }),
      },
    ],
  },
];

type MapLegendProps = {
  scene: MapScene;
  images: Pick<MapImages, "camps" | "heroes">;
  colors: MapColors;
  className?: string;
};

// Легенда карты группами: образцы нарисованы тем же кодом, что карта. Панель поверх карты —
// работает и во весь экран; не помещается — листается.
export const MapLegend = ({ scene, images, colors, className }: MapLegendProps) => {
  const { _ } = useLingui();
  return (
    <div
      role="group"
      aria-label={_(LABEL)}
      className={cn(
        "grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-x-5 gap-y-3 rounded-xl border border-border bg-surface/95 p-3 text-xs text-foreground",
        className,
      )}
    >
      {GROUPS.map((group) => (
        <ul key={group.title.id} aria-label={_(group.title)} className="flex flex-col gap-1">
          <li
            aria-hidden="true"
            className="text-[11px] font-semibold tracking-wide text-subtle uppercase"
          >
            {_(group.title)}
          </li>
          {group.items.map((item) => (
            <li key={item.label.id} className="flex items-center gap-2">
              <LegendSwatch swatch={item.swatch} scene={scene} images={images} colors={colors} />
              {_(item.label)}
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
};
